import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import { useI18n, STR } from "../i18n";
import { COMPANY, IMAGES, CATEGORIES } from "../mock";
import { MessageCircle, Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ nombre: "", empresa: "", telefono: "", correo: "", ciudad: "", categoria: "", mensaje: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    const producto = params.get("producto");
    if (producto) {
      setForm((f) => ({
        ...f,
        categoria: params.get("categoria") || f.categoria,
        mensaje: t({ es: `Me interesa cotizar: ${producto}`, en: `I'd like a quote for: ${producto}` }),
      }));
    }
  }, []); // eslint-disable-line

  const buildText = () => {
    return `${t({ es: "Nueva solicitud de cotización", en: "New quote request" })}%0A%0A` +
      `${t({ es: "Nombre", en: "Name" })}: ${form.nombre}%0A` +
      `${t({ es: "Empresa", en: "Company" })}: ${form.empresa}%0A` +
      `${t({ es: "Teléfono", en: "Phone" })}: ${form.telefono}%0A` +
      `${t({ es: "Correo", en: "Email" })}: ${form.correo}%0A` +
      `${t({ es: "Ciudad", en: "City" })}: ${form.ciudad}%0A` +
      `${t({ es: "Categoría", en: "Category" })}: ${form.categoria}%0A%0A` +
      `${form.mensaje}`;
  };

  const submit = async (e, channel) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.mensaje.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang, channel }),
      });
      if (!res.ok) throw new Error("request failed");
      if (channel === "wa") window.open(`https://wa.me/${COMPANY.phones[0].wa}?text=${buildText()}`, "_blank");
      setSent(true);
    } catch {
      alert(t({ es: "No se pudo enviar la solicitud. Intente de nuevo.", en: "Could not send the request. Please try again." }));
    }
    setSending(false);
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <SEO title={{ es: "Contacto", en: "Contact" }} path="/contacto" description={{ es: "WhatsApp, formulario o correo: elija el canal y respondemos con cotización en 24h.", en: "WhatsApp, form or email: pick a channel and we reply with a quote within 24h." }} />
      <section className="hero phero">
        <div className="bg" style={{ backgroundImage: `url(${IMAGES.workers})` }} />
        <div className="shade" />
        <div className="container-x inner">
          <Breadcrumbs items={[{ label: { es: "Inicio", en: "Home" }, href: "/" }, { label: { es: "Contacto", en: "Contact" } }]} />
          <span className="eyebrow">{t({ es: "Contacto", en: "Contact" })}</span>
          <h1>{t({ es: "Hablemos de su requerimiento", en: "Let's talk about your requirement" })}</h1>
          <p className="lead">{t({ es: "WhatsApp, formulario o correo: elija el canal y respondemos con cotización y seguimiento.", en: "WhatsApp, form or email: pick a channel and we reply with a quote and follow-up." })}</p>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <div className="grid-3">
            <Reveal>
              <div className="card" style={{ padding: 26 }}>
                <h3><MessageCircle size={20} style={{ display: "inline", verticalAlign: "-3px", color: "var(--wa)", marginRight: 8 }} />WhatsApp / {t({ es: "Teléfonos", en: "Phones" })}</h3>
                <ul style={{ listStyle: "none", padding: 0, lineHeight: 2 }}>
                  {COMPANY.phones.map(p => (
                    <li key={p.wa}><span className="chip" style={{ marginRight: 8, fontSize: 11 }}>{p.flag}</span><a href={`https://wa.me/${p.wa}`} target="_blank" rel="noopener noreferrer" style={{ color: "var(--navy)", textDecoration: "none", fontWeight: 600 }}>{p.label}</a></li>
                  ))}
                </ul>
                <a href={`https://wa.me/${COMPANY.phones[0].wa}`} target="_blank" rel="noopener noreferrer" className="btn btn-wa" style={{ marginTop: 14 }}><MessageCircle size={16} /> {t(STR.cta.whatsapp)}</a>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="card" style={{ padding: 26 }}>
                <h3><Mail size={20} style={{ display: "inline", verticalAlign: "-3px", color: "var(--navy)", marginRight: 8 }} />{t({ es: "Correo y ubicación", en: "Email & location" })}</h3>
                <ul style={{ listStyle: "none", padding: 0, lineHeight: 2, fontSize: 14 }}>
                  <li><b>Email:</b><br /><a href={`mailto:${COMPANY.email}`} style={{ color: "var(--navy)" }}>{COMPANY.email}</a></li>
                  <li style={{ marginTop: 10 }}><b>{t({ es: "Direcciones", en: "Addresses" })}:</b><br />{COMPANY.addresses.map((a) => <span key={a.flag} style={{ display: "block", marginTop: 4 }}>[{a.flag}] {a.text}</span>)}</li>
                  <li style={{ marginTop: 10 }}><b>Web:</b><br /><a href="https://altamiravenezuela.com" style={{ color: "var(--navy)" }}>{COMPANY.web}</a></li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={160}>
              <div className="card" style={{ padding: 26 }}>
                <h3>{t({ es: "Redes oficiales", en: "Official social" })}</h3>
                <ul style={{ listStyle: "none", padding: 0, lineHeight: 2, fontSize: 14 }}>
                  <li><a href={COMPANY.social.instagram} target="_blank" rel="noopener noreferrer" style={{ color: "var(--navy)" }}>Instagram — @altamira_inversiones</a></li>
                  <li><a href={COMPANY.social.facebook} target="_blank" rel="noopener noreferrer" style={{ color: "var(--navy)" }}>Facebook — Grupo Inversiones Altamira</a></li>
                  <li><a href={COMPANY.social.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: "var(--navy)" }}>LinkedIn — Grupo Inversiones Altamira C.A.</a></li>
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal className="sh" style={{ marginTop: 80, marginBottom: 30 }}><h2>{t({ es: "Formulario de cotización", en: "Quote form" })}</h2><p>{t({ es: "Complete el formulario y nuestro equipo recibirá su solicitud al instante.", en: "Fill in the form and our team will receive your request instantly." })}</p></Reveal>

          {sent ? (
            <Reveal>
              <div className="card" style={{ padding: 40, textAlign: "center", maxWidth: 640, margin: "0 auto" }}>
                <CheckCircle2 size={48} style={{ color: "var(--wa)", margin: "0 auto 16px" }} />
                <h3 data-testid="contact-success-message">{t({ es: "¡Solicitud recibida!", en: "Request received!" })}</h3>
                <p>{t({ es: "Guardamos su solicitud y nuestro equipo fue notificado. Le responderemos con una cotización en menos de 24 horas.", en: "We saved your request and our team was notified. We will reply with a quote within 24 hours." })}</p>
                <button onClick={() => { setSent(false); setForm({ nombre: "", empresa: "", telefono: "", correo: "", ciudad: "", categoria: "", mensaje: "" }); }} className="btn btn-navy" style={{ marginTop: 16 }}>{t({ es: "Nueva solicitud", en: "New request" })}</button>
              </div>
            </Reveal>
          ) : (
          <Reveal>
            <form className="form-grid" style={{ maxWidth: 900, margin: "0 auto" }}>
              <div className="field"><label>{t({ es: "Nombre", en: "Name" })}</label><input data-testid="contact-name-input" value={form.nombre} onChange={upd("nombre")} required /></div>
              <div className="field"><label>{t({ es: "Empresa", en: "Company" })}</label><input value={form.empresa} onChange={upd("empresa")} /></div>
              <div className="field"><label>{t({ es: "Teléfono / WhatsApp", en: "Phone / WhatsApp" })}</label><input value={form.telefono} onChange={upd("telefono")} placeholder="+58..." /></div>
              <div className="field"><label>{t({ es: "Correo", en: "Email" })}</label><input type="email" value={form.correo} onChange={upd("correo")} /></div>
              <div className="field"><label>{t({ es: "Ciudad / país", en: "City / country" })}</label><input value={form.ciudad} onChange={upd("ciudad")} /></div>
              <div className="field">
                <label>{t({ es: "Categoría de interés", en: "Category of interest" })}</label>
                <select value={form.categoria} onChange={upd("categoria")}>
                  <option value="">{t({ es: "Seleccione...", en: "Select..." })}</option>
                  {CATEGORIES.map(c => <option key={c.id} value={t(c.title)}>{t(c.title)}</option>)}
                  <option value={t({ es: "Otro", en: "Other" })}>{t({ es: "Otro", en: "Other" })}</option>
                </select>
              </div>
              <div className="field full"><label>{t({ es: "Mensaje", en: "Message" })}</label><textarea data-testid="contact-message-input" value={form.mensaje} onChange={upd("mensaje")} placeholder={t({ es: "Cuéntenos su requerimiento...", en: "Tell us about your requirement..." })} /></div>
              <div className="full" style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center", marginTop: 10 }}>
                <button type="submit" data-testid="contact-submit-button" onClick={(e) => submit(e, "form")} disabled={sending} className="btn btn-navy"><Send size={16} /> {sending ? t({ es: "Enviando...", en: "Sending..." }) : t({ es: "Enviar solicitud", en: "Send request" })}</button>
                <button type="submit" data-testid="contact-whatsapp-button" onClick={(e) => submit(e, "wa")} disabled={sending} className="btn btn-wa"><MessageCircle size={16} /> {t({ es: "Enviar y abrir WhatsApp", en: "Send & open WhatsApp" })}</button>
              </div>
              <p className="full" style={{ textAlign: "center", fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{t({ es: "¿Necesita adjuntar archivos? Envíelos a info@equipmentlivechat.com o por WhatsApp.", en: "Need to attach files? Send them to info@equipmentlivechat.com or via WhatsApp." })}</p>
            </form>
          </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
